import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import {
  ExternalLink,
  IconArrowHead,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
} from '../../../../../components';

import css from './LinksMenu.module.css';
import IconCollection from '../../../../../components/IconCollection/IconCollection';

/**
 * Link components to be shown inside the dropdown.
 *
 * @param {*} props contain keys: linkConfig, currentPage
 * @returns NamedLink or ExternalLink
 */
export const LinkComponent = ({ linkConfig, currentPage, setHasError }) => {
  const { text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;
    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // set initial value
    const mediaQueryList = window.matchMedia('(max-width: 767px)');
    setIsMobile(mediaQueryList.matches);

    //watch for updates
    function updateIsMobile(e) {
      setIsMobile(e.matches);
    }
    
    mediaQueryList.addEventListener('change', updateIsMobile);

    // clean up after ourselves
    return function cleanup() {
      mediaQueryList.removeEventListener('change', updateIsMobile);
    };
  }, []);

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};

    const mobileEditListingsLink = isMobile && name === "EditListingPage"

    const className = classNames(css.menuLink, getCurrentPageClass(name));
    return (
      <>
        {mobileEditListingsLink ? <>
        <button type="button" className={className} onClick={() => {
            setHasError(true);
          }}>
          <span className={css.menuItemBorder} />
          {text}
        </button></> : <NamedLink name={name} params={params} to={to} className={className}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>}
      </>
    );
  }
  return (
    <ExternalLink href={href} className={css.menuLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * When the links menu shows "More" label (instead of "Menu"), the label width needs to be measured.
 *
 * @param {*} props containing: width, setWidth, label
 * @returns div with same styles as the real "More" label or null if width is known.
 */
const MeasureMoreMenu = props => {
  const { width, setWidth, label } = props;
  const moreMenuRef = useRef(null);
  useEffect(() => {
    if (moreMenuRef.current && !width) {
      setWidth(moreMenuRef.current.offsetWidth);
    }
  }, [moreMenuRef, width]);

  const isServer = typeof window === 'undefined';
  // Component is measured outside of the viewport
  const styleWrapper = !!width
    ? {}
    : {
      style: {
        position: 'absolute',
        top: '-2000px',
        left: '-2000px',
        width: 'auto', // The content defines width
        height: 'var(--topbarHeightDesktop)',
        display: 'flex',
        flexDirection: 'row',
      },
    };

  return !width && !isServer
    ? ReactDOM.createPortal(
      <div
        id="measureMoreLabel"
        className={css.linkMenuLabel}
        ref={moreMenuRef}
        {...styleWrapper}
      >
        {label}
      </div>,
      document.body
    )
    : null;
};

/**
 * Menu label has text (Menu vs More) and arrow up vs down
 *
 * @param {*} props contain keys: showMoreLabel, isOpen, intl
 * @returns span containing menu label text and IconArrowHead
 */
const MenuLabelContent = ({ showMoreLabel, isOpen, intl }) => (
  <span className={css.linkMenuLabelWrapper}>
    {showMoreLabel
      ? intl.formatMessage({ id: 'TopbarDesktop.LinksMenu.more' })
      : intl.formatMessage({ id: 'TopbarDesktop.LinksMenu.all' })}
    <IconArrowHead direction="down" size="small" rootClassName={css.arrowIcon} />
  </span>
);

/**
 * Menu that shows custom links with label showing either "Menu" or "More".
 * The component also measures the width of the "More" label.
 *
 * @param {*} props contain: id, currentPage, links, showMoreLabel, moreLabelWidth, setMoreLabelWidth, intl
 * @returns menu component
 */
const LinksMenu = props => {
  const [isOpen, setIsOpen] = useState(false);
  // const [hasError, setHasError] = useState(false);
  const { id, currentPage, links, showMoreLabel, moreLabelWidth, setMoreLabelWidth, intl, setHasError } = props;
  const contentPlacementOffset = moreLabelWidth ? -1 * (moreLabelWidth / 2) : 24;

  return (
    <>
      <Menu
        id={id}
        contentPlacementOffset={contentPlacementOffset}
        contentPosition="left"
        isOpen={isOpen}
        onToggleActive={setIsOpen}
      >
        <MenuLabel className={classNames(css.linkMenuLabel, css.addNewListing)} isOpenClassName={css.linkMenuIsOpen}>
          <div className={css.topbarLink}>
            <span className={css.topbarLinkLabel}>
              <NamedLink
                className={css.createListingLink}
                name="NewListingPage"
                title="Add New Content"
                aria-label="Add New Content"
              >
                <IconCollection icon="plus-icon" />
              </NamedLink>
            </span>
          </div>
        </MenuLabel>
        <MenuContent className={css.linkMenuContent}>
          {links.map(linkConfig => {
            return (
              <MenuItem key={linkConfig.text} className={css.menuItem}>
                <LinkComponent linkConfig={linkConfig} currentPage={currentPage} setHasError={setHasError} />
              </MenuItem>
            );
          })}
        </MenuContent>
      </Menu>
      <MeasureMoreMenu
        width={moreLabelWidth}
        setWidth={setMoreLabelWidth}
        label={<MenuLabelContent showMoreLabel={true} intl={intl} />}
      />
    </>
  );
};

export default LinksMenu;
